import { transactionService } from '@/services/TransactionService';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildTransactionCompletionReportHtml,
  type TransactionCompletionItem,
  type TransactionCompletionTemplateOptions,
} from '@/reports/templates/transactionCompletionTemplate';
import type { AssetTransactionRecord } from '@/types/Transactions';

const INCLUDED_TRANSACTION_TYPES = new Set(['ISSUE_OUT', 'TURN_IN', 'EXCHANGE']);

const toSeconds = (value: string | null): number | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.floor(date.getTime() / 1000);
};

const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const hh = hours.toString().padStart(2, '0');
  const mm = minutes.toString().padStart(2, '0');
  const ss = secs.toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const formatTransactionTypeLabel = (value: AssetTransactionRecord['transactionType']): string => {
  if (!value) return '—';
  const lower = String(value).toLowerCase();
  if (lower === 'issue_out') return 'Issue';
  if (lower === 'turn_in') return 'Turn In';
  if (lower === 'non_recoverable_clear') return 'Non-Recoverable';
  if (lower === 'exchange') return 'DX';
  return value
    .toString()
    .toLowerCase()
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const resolveUserLabel = (record: AssetTransactionRecord): string => {
  return (
    record.performedByName?.trim() || record.createdBy?.trim() || record.updatedBy?.trim() || '—'
  );
};

export const runTransactionCompletionReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const fromDate = (params.fromDate as string | undefined) ?? '';
  const toDate = (params.toDate as string | undefined) ?? '';
  const userEmail = (params.userEmail as string | undefined) ?? '';

  const records = await transactionService.listLatest(500, {
    from: fromDate || undefined,
    to: toDate || undefined,
    types: Array.from(INCLUDED_TRANSACTION_TYPES).join(','),
  });

  const filtered = records.filter((record) => {
    if (!record.transactionType || !INCLUDED_TRANSACTION_TYPES.has(record.transactionType)) {
      return false;
    }

    if (!record.processedAt) {
      return false;
    }

    const userCandidates = [record.performedByName, record.createdBy, record.updatedBy]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.toLowerCase());

    if (userEmail && !userCandidates.some((value) => value.includes(userEmail.toLowerCase()))) {
      return false;
    }

    return true;
  });

  const items: TransactionCompletionItem[] = [];
  const durations: number[] = [];

  filtered.forEach((record) => {
    const createdTs = toSeconds(record.createdAt);
    const processedTs = toSeconds(record.processedAt);
    if (createdTs == null || processedTs == null) {
      return;
    }
    const duration = processedTs - createdTs;
    durations.push(duration);

    items.push({
      user: resolveUserLabel(record),
      elapsedLabel: formatDuration(duration),
      transactionDate: record.processedAt ? (record.processedAt.split('T')[0] ?? '') : '',
      transactionTypeLabel: formatTransactionTypeLabel(record.transactionType),
    });
  });

  const averageSeconds =
    durations.length > 0 ? durations.reduce((sum, value) => sum + value, 0) / durations.length : 0;

  const options: TransactionCompletionTemplateOptions = {
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
      userEmail: userEmail || null,
      averageDurationLabel: durations.length > 0 ? formatDuration(averageSeconds) : null,
    },
    items,
  };

  const html = buildTransactionCompletionReportHtml(options);
  outputReport(
    html,
    'transaction-completion-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
